import { createFSMReducer } from 'app-webtech-core/reducers/fsm';
import {
  AUTOSTART_NOT_ALLOWED,
  END,
  ENTERED_FULLSCREEN,
  EXITED_FULLSCREEN,
  LOAD,
  PAUSE,
  PLAY,
  TIME,
  SEEK,
  SET_ERROR,
  SET_INFO_BANNER,
  START_BUFFERING,
  STOP,
  VOLUME,
  MUTE,
  UNMUTE,
  VOLUME_CHANGE,
  CLEAR_ERROR,
  VIDEO_BIT_RATE_CHANGE,
  SHOW_VIDEO_CONTROLS,
  HIDE_VIDEO_CONTROLS,
  CC_MENU_OPEN,
  CC_MENU_CLOSE,
  CC_MENU_RESET,
  CC_NEXT_SUBMENU,
  CC_PREV_SUBMENU,
  CC_INDEX_CHANGE,
  CC_INDEX_SET,
  SET_IS_VIDEO_REQUESTED,
  EPISODE_ENDS,
  EPISODE_STARTS,
  CLIP_ENDS,
  CLIP_STARTS,
} from '../actions/types';
import { playerAutostartNotAllowedReducer } from './autostartNotAllowed';
import { playerLoadReducer } from './load';
import { playerPlayReducer } from './play';
import { playerPauseReducer } from './pause';
import { playerTimeReducer } from './time';
import { playerStopReducer } from './stop';
import { playerSeekReducer } from './seek';
import { playerStartBufferingReducer } from './startBuffering';
import { playerEndReducer } from './end';
import { playerVolumeReducer } from './volume';
import { playerIsVideoRequestedReducer } from './setIsVideoRequested';
import { playerSetErrorReducer } from './setError';
import { playerSetInfoBannerReducer } from './setInfoBanner';
import { playerEpisodeEndsReducer } from './episodeEnd';
import { playerEpisodeStartsReducer } from './episodeStart';
import { playerClipEndsReducer } from './clipEnd';
import { playerClipStartsReducer } from './clipStart';
import { playerEnderedFullscreenReducer } from './enderedFullscreen';
import { playerExitedFullscreenReducer } from './exitedFullscreen';
import { playerMuteReducer } from './mute';
import { playerUnmuteReducer } from './unmute';
import { playerVolumeChangeReducer } from './volumeChange';
import { videoBitRateChangeReducer } from './videoBitRateChange';
import { playerHideVideoControlsReducer } from './hideVideoControls';
import { playerShowVideoControlsReducer } from './showVideoControls';
import { playerCCMenuOpenReducer } from './ccMenuOpen';
import { playerCCMenuCloseReducer } from './ccMenuClose';
import { playerCCMenuResetReducer } from './ccMenuReset';
import { playerCCSubmenuChangeReducer } from './ccSubmenuChange';
import { playerCCMenuChangeIndexReducer } from './ccMenuChangeIndex';
import { playerCCMenuSetIndexReducer } from './ccMenuSetIndex';
import { PLAYER_STATE_STATUSES, CCSETTINGSMENU } from '../constants';
import { getName } from '../actions/helpers/ccMenuIndexHelper';

export const defaultState = {
  status: PLAYER_STATE_STATUSES.IDLE,
  content: null,
  startTime: null,
  isFullscreen: true,
  bufferingLogs: [],
  error: null,
  episodeIsOver: true,
  clipIsOver: true,
  mute: false,
  volume: 100,
  volumeBeforeMute: 100,
  videoRequested: false,
  bitrate: -1,
  showVideoControls: true,
  isCCMenuOpen: false,
  prevCCMenu: null,
  prevCCMenuName: '',
  currentCCMenu: CCSETTINGSMENU,
  currentCCMenuName: getName(CCSETTINGSMENU[0]),
  ccMenuSelectedIndexes: [],
  isPaused: false,
};

const independentTypes = [
  SET_INFO_BANNER,
  ENTERED_FULLSCREEN,
  EXITED_FULLSCREEN,
  MUTE,
  UNMUTE,
  VOLUME_CHANGE,
  VOLUME,
  VIDEO_BIT_RATE_CHANGE,
  SHOW_VIDEO_CONTROLS,
  HIDE_VIDEO_CONTROLS,
  SET_IS_VIDEO_REQUESTED,
  EPISODE_STARTS,
  EPISODE_ENDS,
  CLIP_ENDS,
  CLIP_STARTS,
];

const params = {
  initial: PLAYER_STATE_STATUSES.IDLE,
  actionReducers: {
    [AUTOSTART_NOT_ALLOWED]: {
      reducer: playerAutostartNotAllowedReducer,
      next: PLAYER_STATE_STATUSES.AUTOSTART_NOT_ALLOWED,
    },
    [LOAD]: {
      reducer: playerLoadReducer,
      next: PLAYER_STATE_STATUSES.LOADING,
    },
    [PLAY]: {
      reducer: playerPlayReducer,
      next: PLAYER_STATE_STATUSES.PLAYING,
    },
    [PAUSE]: {
      reducer: playerPauseReducer,
      next: PLAYER_STATE_STATUSES.PAUSED,
    },
    [TIME]: playerTimeReducer,
    [STOP]: {
      reducer: playerStopReducer,
      next: PLAYER_STATE_STATUSES.STOPPED,
    },
    [SEEK]: {
      reducer: playerSeekReducer,
      next: PLAYER_STATE_STATUSES.SEEKING,
    },
    [START_BUFFERING]: {
      reducer: playerStartBufferingReducer,
      next: PLAYER_STATE_STATUSES.BUFFERING,
    },
    [END]: {
      reducer: playerEndReducer,
      next: PLAYER_STATE_STATUSES.ENDED,
    },
    [VOLUME]: playerVolumeReducer,
    [SET_ERROR]: {
      reducer: playerSetErrorReducer,
      next: PLAYER_STATE_STATUSES.ERROR,
    },
    [CLEAR_ERROR]: {
      reducer: playerSetErrorReducer,
      next: PLAYER_STATE_STATUSES.IDLE,
    },
    [EPISODE_ENDS]: playerEpisodeEndsReducer,
    [EPISODE_STARTS]: playerEpisodeStartsReducer,
    [CLIP_ENDS]: playerClipEndsReducer,
    [CLIP_STARTS]: playerClipStartsReducer,
    [SET_INFO_BANNER]: playerSetInfoBannerReducer,
    [ENTERED_FULLSCREEN]: playerEnderedFullscreenReducer,
    [EXITED_FULLSCREEN]: playerExitedFullscreenReducer,
    [MUTE]: playerMuteReducer,
    [UNMUTE]: playerUnmuteReducer,
    [VOLUME_CHANGE]: playerVolumeChangeReducer,
    [VIDEO_BIT_RATE_CHANGE]: videoBitRateChangeReducer,
    [SET_IS_VIDEO_REQUESTED]: playerIsVideoRequestedReducer,
    [SHOW_VIDEO_CONTROLS]: playerShowVideoControlsReducer,
    [HIDE_VIDEO_CONTROLS]: playerHideVideoControlsReducer,
    [CC_MENU_OPEN]: playerCCMenuOpenReducer,
    [CC_MENU_CLOSE]: playerCCMenuCloseReducer,
    [CC_NEXT_SUBMENU]: playerCCSubmenuChangeReducer,
    [CC_PREV_SUBMENU]: playerCCSubmenuChangeReducer,
    [CC_INDEX_CHANGE]: playerCCMenuChangeIndexReducer,
    [CC_MENU_RESET]: playerCCMenuResetReducer,
    [CC_INDEX_SET]: playerCCMenuSetIndexReducer,
  },

  states: {
    [PLAYER_STATE_STATUSES.AUTOSTART_NOT_ALLOWED]: {
      accepts: [...independentTypes, START_BUFFERING, SET_ERROR],
    },
    [PLAYER_STATE_STATUSES.IDLE]: {
      accepts: [
        ...independentTypes,
        LOAD,
        PLAY,
        SET_ERROR,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.LOADING]: {
      accepts: [
        ...independentTypes,
        AUTOSTART_NOT_ALLOWED,
        LOAD,
        PLAY,
        START_BUFFERING,
        SET_ERROR,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.PLAYING]: {
      accepts: [
        ...independentTypes,
        LOAD,
        PAUSE,
        TIME,
        STOP,
        SEEK,
        START_BUFFERING,
        END,
        SET_ERROR,
        SET_INFO_BANNER,
        SHOW_VIDEO_CONTROLS,
        HIDE_VIDEO_CONTROLS,
        ENTERED_FULLSCREEN,
        EXITED_FULLSCREEN,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.PAUSED]: {
      accepts: [
        ...independentTypes,
        LOAD,
        PLAY,
        START_BUFFERING,
        STOP,
        SEEK,
        TIME,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.BUFFERING]: {
      accepts: [
        ...independentTypes,
        LOAD,
        PLAY,
        PAUSE,
        SET_ERROR,
        ENTERED_FULLSCREEN,
        EXITED_FULLSCREEN,
        SET_INFO_BANNER,
        SHOW_VIDEO_CONTROLS,
        HIDE_VIDEO_CONTROLS,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.SEEKING]: {
      accepts: [
        ...independentTypes,
        LOAD,
        PLAY,
        SET_ERROR,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.STOPPED]: {
      accepts: [
        ...independentTypes,
        LOAD,
        END,
        SET_ERROR,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.ENDED]: {
      accepts: [
        ...independentTypes,
        LOAD,
        SET_ERROR,
        CC_MENU_OPEN,
        CC_MENU_CLOSE,
        CC_MENU_RESET,
        CC_NEXT_SUBMENU,
        CC_PREV_SUBMENU,
        CC_INDEX_CHANGE,
        CC_INDEX_SET,
      ],
    },
    [PLAYER_STATE_STATUSES.ERROR]: {
      accepts: [...independentTypes, CLEAR_ERROR, LOAD, PLAY],
    },
  },
};

export const playerReducer = createFSMReducer(defaultState, params);
